// ============================================================================
// Overall Control — display helpers and the status colour scale.
// Colour is used ONLY for status (brief §4.5): green done, amber at risk (major darker, minor
// lighter), red breached, violet undelivered. Everything else stays neutral.
// ============================================================================
import dayjs from "dayjs";
import { TONNES_FROM_KG } from "./config/rules";
import { OcStatus } from "./types";

export const STATUS_META: Record<OcStatus, { label: string; color: string; text: string }> = {
  done_on_time: { label: "On time", color: "#52c41a", text: "#237804" },
  done_late: { label: "Done late", color: "#b7eb8f", text: "#5b8c00" },
  on_track: { label: "On track", color: "#d9d9d9", text: "#595959" },
  at_risk_minor: { label: "At risk · minor", color: "#ffd666", text: "#ad6800" },
  at_risk_major: { label: "At risk · major", color: "#fa8c16", text: "#ad4e00" },
  breached: { label: "Breached", color: "#f5222d", text: "#a8071a" },
  undelivered: { label: "Undelivered", color: "#9254de", text: "#531dab" },
};

// Legend / distribution-bar order (brief §4.3.1).
export const STATUS_ORDER: OcStatus[] = [
  "done_on_time", "done_late", "on_track", "at_risk_minor", "at_risk_major", "breached", "undelivered",
];

export const fmtWeight = (kg: number | null | undefined): string => {
  if (kg == null) return "—";
  if (kg >= TONNES_FROM_KG) return `${(kg / 1000).toFixed(kg >= 10 * TONNES_FROM_KG ? 0 : 1)} Ton`;
  return `${Math.round(kg)} kg`;
};

export const fmtDuration = (minutes: number | null | undefined): string => {
  if (minutes == null) return "—";
  const m = Math.abs(Math.round(minutes));
  const sign = minutes < 0 ? "−" : "";
  if (m < 60) return `${sign}${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${sign}${h}h ${m % 60 ? `${m % 60}m` : ""}`.trim();
  return `${sign}${Math.floor(h / 24)}d ${h % 24}h`;
};

// Signed delay for a cell: "+1h 20m" late, "−15m" early.
export const fmtDelay = (minutes: number | null | undefined): string => {
  if (minutes == null) return "";
  if (minutes > 0) return `+${fmtDuration(minutes)}`;
  return fmtDuration(minutes);
};

// "14:05" for today, "Fri 14:05" within a week, "3 Oct 14:05" otherwise.
export const fmtWhen = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = dayjs(iso);
  const now = dayjs();
  if (d.isSame(now, "day")) return d.format("HH:mm");
  if (Math.abs(d.diff(now, "day")) < 6) return d.format("ddd HH:mm");
  return d.format("D MMM HH:mm");
};

export const fmtPct = (n: number | null | undefined) => (n == null ? "—" : `${Math.round(n)}%`);
